import { Suspense, lazy } from "react"
import { Navigate, Route, Routes } from "react-router"
import { authRoutes } from "app/router/auth/routes"
import { GuestOnly } from "app/router/guards/GuestOnly"
import { RequireAuth } from "app/router/guards/RequireAuth"
import { PrivateLayout } from "app/router/private/Layout"
import {
  privateRoutes,
  type PrivatePage,
  type PrivateRouteDefinition,
} from "app/router/private/routes"
import { PublicLayout } from "app/router/public/Layout"
import { isPublicPageDisabled, publicRoutes } from "app/router/public/routes"
import { usePageNavigation } from "app/router/public/useNavigation"
import type { PublicPage } from "app/router/public/types"
import { usePageMetadata } from "shared/hooks/usePageMetadata"
import { PageLoadingFallback } from "shared/ui/PageLoadingFallback"

const LoginPage = lazy(() =>
  import("pages/auth/login").then((module) => ({
    default: module.LoginPage,
  })),
)

const RegisterPage = lazy(() =>
  import("pages/auth/register").then((module) => ({
    default: module.RegisterPage,
  })),
)

const DebtorRegisterPage = lazy(() =>
  import("pages/auth/register").then((module) => ({
    default: module.DebtorRegisterPage,
  })),
)

const ForgotPasswordPage = lazy(() =>
  import("pages/auth/forgot-password").then((module) => ({
    default: module.ForgotPasswordPage,
  })),
)

const ResetPasswordPage = lazy(() =>
  import("pages/auth/reset-password").then((module) => ({
    default: module.ResetPasswordPage,
  })),
)

const IssuerActivationPage = lazy(() =>
  import("pages/auth/issuer-activation").then((module) => ({
    default: module.IssuerActivationPage,
  })),
)

function PublicRouteElement({ page }: { page: PublicPage }) {
  const navigateToPage = usePageNavigation()
  const isDisabledPage = isPublicPageDisabled(page)
  const pageDefinition = publicRoutes[isDisabledPage ? "home" : page]
  const CurrentPage = pageDefinition.component

  usePageMetadata({
    title: pageDefinition.title,
    description: pageDefinition.description,
  })

  if (isDisabledPage) {
    return <Navigate to={publicRoutes.home.path} replace />
  }

  return (
    <Suspense fallback={<PageLoadingFallback minHeight={420} />}>
      <CurrentPage onNavigate={navigateToPage} />
    </Suspense>
  )
}

function PrivateRouteElement({ page }: { page: PrivatePage }) {
  const pageDefinition = privateRoutes[page]
  const CurrentPage = pageDefinition.component

  usePageMetadata({
    title: pageDefinition.title,
    description: pageDefinition.description,
  })

  return (
    <Suspense fallback={<PageLoadingFallback minHeight={360} />}>
      <CurrentPage />
    </Suspense>
  )
}

export function AppRouter() {
  const privateRouteEntries = Object.entries(privateRoutes) as Array<
    [PrivatePage, PrivateRouteDefinition]
  >

  return (
    <Routes>
      <Route element={<PublicLayout />}>
        {Object.entries(publicRoutes).map(([page, definition]) => (
          <Route
            key={definition.path}
            path={definition.path}
            element={<PublicRouteElement page={page as PublicPage} />}
          />
        ))}
      </Route>

      <Route element={<GuestOnly />}>
        <Route
          path={authRoutes.login.path}
          element={
            <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}>
              <LoginPage />
            </Suspense>
          }
        />
        <Route
          path={authRoutes.registro.path}
          element={
            <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}>
              <RegisterPage />
            </Suspense>
          }
        />
        <Route
          path={authRoutes.registroDevedor.path}
          element={
            <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}>
              <DebtorRegisterPage />
            </Suspense>
          }
        />
        <Route
          path={authRoutes.esqueceuSenha.path}
          element={
            <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}>
              <ForgotPasswordPage />
            </Suspense>
          }
        />
        <Route
          path={authRoutes.redefinirSenha.path}
          element={
            <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}>
              <ResetPasswordPage />
            </Suspense>
          }
        />
        <Route
          path={authRoutes.ativacaoEmissor.path}
          element={
            <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}>
              <IssuerActivationPage />
            </Suspense>
          }
        />
      </Route>

      <Route
        path="/acesso/registro-investidor"
        element={<Navigate to={publicRoutes.titulos.path} replace />}
      />

      <Route
        path="/portal/investidor/*"
        element={<Navigate to={publicRoutes.titulos.path} replace />}
      />

      <Route element={<RequireAuth />}>
        <Route element={<PrivateLayout />}>
          {privateRouteEntries.map(([page, definition]) => (
            <Route
              key={definition.path}
              element={<RequireAuth allowedRoles={definition.allowedRoles} />}
            >
              <Route
                path={definition.path}
                element={<PrivateRouteElement page={page as PrivatePage} />}
              />
            </Route>
          ))}
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
